import pc from 'picocolors';

export interface StateResource {
  kind: string;
  name: string;
  status: string;
  metadata?: Record<string, unknown>;
  updatedAt?: string | Date;
}

const STATUS_ICON: Record<string, string> = {
  ready: '✔',
  pending: '…',
  failed: '✘',
};

function info(resource: StateResource): string {
  const meta = resource.metadata ?? {};
  if (resource.status === 'failed' && typeof meta.error === 'string') return meta.error;
  for (const key of ['repoUrl', 'url', 'uri', 'triggerId', 'schema']) {
    if (typeof meta[key] === 'string') return meta[key] as string;
  }
  return '';
}

/**
 * Render block state rows as a fixed-width table. `color: false` for tests
 * and --json-adjacent contexts.
 */
export function renderStateTable(resources: StateResource[], color = true): string {
  const paint = (status: string, text: string): string => {
    if (!color) return text;
    if (status === 'ready') return pc.green(text);
    if (status === 'failed') return pc.red(text);
    return pc.yellow(text);
  };

  const rows = resources.map((r) => ({
    block: r.kind,
    name: r.name,
    status: `${STATUS_ICON[r.status] ?? '?'} ${r.status}`,
    info: info(r),
    rawStatus: r.status,
  }));

  const header = { block: 'BLOCK', name: 'NAME', status: 'STATUS', info: 'INFO' };
  const width = (key: 'block' | 'name' | 'status'): number =>
    Math.max(header[key].length, ...rows.map((r) => r[key].length));
  const wBlock = width('block');
  const wName = width('name');
  const wStatus = width('status');

  const lines: string[] = [];
  const headerLine = `${header.block.padEnd(wBlock)}  ${header.name.padEnd(wName)}  ${header.status.padEnd(wStatus)}  ${header.info}`;
  lines.push(color ? pc.bold(headerLine) : headerLine);
  for (const r of rows) {
    lines.push(
      `${r.block.padEnd(wBlock)}  ${r.name.padEnd(wName)}  ${paint(r.rawStatus, r.status.padEnd(wStatus))}  ${r.info}`.trimEnd(),
    );
  }
  return lines.join('\n');
}

export function summarizeState(resources: StateResource[]): {
  total: number;
  ready: number;
  failed: number;
  pending: number;
} {
  return {
    total: resources.length,
    ready: resources.filter((r) => r.status === 'ready').length,
    failed: resources.filter((r) => r.status === 'failed').length,
    pending: resources.filter((r) => r.status !== 'ready' && r.status !== 'failed').length,
  };
}
